import React, { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useDebounce } from 'use-debounce/lib';
import { CircularProgress } from '@material-ui/core';
import MetricsTable from '../common/table/MetricsTable';
import EnhancedTableToolbar from '../common/table/EnhancedTableToolbar';
import Error from '../common/Error';

const withMetricsByTaxonomy = WrappedMetricsTable => props => {
  const { selectedId, page, setPage } = props;
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [metricNameFilter, setMetricNameFilter] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [debouncedMetricNameFilter] = useDebounce(metricNameFilter, 250);
  const getTaxonomyFilter = () => (selectedId ? { id: selectedId } : null);
  const getMetricFilter = () =>
    debouncedMetricNameFilter && debouncedMetricNameFilter.length > 0 ? { lowercaseName_contains: debouncedMetricNameFilter.toLowerCase() } : null;

  const METRICS_PAGINATE_QUERY = gql`
    query metricsClassifiedAsQuery(
      $first: Int
      $offset: Int
      $orderBy: [_MetricOrdering]
      $taxonomyFilter: _TaxonomyFilter
      $metricFilter: _MetricFilter
    ) {
      Taxonomy(filter: $taxonomyFilter) {
        name
        metrics(first: $first, offset: $offset, orderBy: $orderBy, filter: $metricFilter) {
          id
          name
          unit
          multiplier
          datapointCount
          geographies {
            id
            name
          }
        }
      }
    }
  `;

  const paginateQueryResult = useQuery(METRICS_PAGINATE_QUERY, {
    variables: {
      first: rowsPerPage,
      offset: rowsPerPage * page,
      orderBy: orderBy + '_' + order,
      taxonomyFilter: getTaxonomyFilter(),
      metricFilter: getMetricFilter()
    }
  });

  const metricCountQueryResult = useQuery(
    gql`
      query metricsClassifiedAsCountQuery($taxonomyFilter: _TaxonomyFilter, $metricFilter: _MetricFilter) {
        Taxonomy(filter: $taxonomyFilter) {
          metrics(filter: $metricFilter) {
            id
          }
        }
      }
    `,
    {
      variables: {
        taxonomyFilter: getTaxonomyFilter(),
        metricFilter: getMetricFilter()
      }
    }
  );

  const totalMetricCount =
    metricCountQueryResult.data &&
    metricCountQueryResult.data.Taxonomy &&
    metricCountQueryResult.data.Taxonomy.length > 0 &&
    metricCountQueryResult.data.Taxonomy[0] &&
    metricCountQueryResult.data.Taxonomy[0].metrics &&
    metricCountQueryResult.data.Taxonomy[0].metrics.length > 0
      ? metricCountQueryResult.data.Taxonomy[0].metrics.length
      : 0;

  const { error, loading, data } = paginateQueryResult;
  const taxonomy = data && data.Taxonomy && data.Taxonomy.length > 0 && data.Taxonomy[0];
  const metrics = taxonomy && taxonomy.metrics ? taxonomy.metrics : null;
  const taxonomyName = taxonomy && taxonomy.name;

  return (
    <>
      <EnhancedTableToolbar
        title={taxonomyName}
        metricNameFilter={metricNameFilter}
        onMetricNameFilterChanged={setMetricNameFilter}
        showSearchField
      />
      {loading && <CircularProgress />}
      {error && <Error />}
      {!loading && !error && metrics && (
        <WrappedMetricsTable
          {...props}
          metrics={metrics}
          totalMetricCount={totalMetricCount}
          onChangePage={(_, newPage) => setPage(newPage)}
          onChangeRowsPerPage={event => {
            setPage(0);
            setRowsPerPage(parseInt(event.target.value, 10));
          }}
          page={page}
          rowsPerPage={rowsPerPage}
          onChangeOrder={(newOrder, newOrderBy) => {
            setOrder(newOrder);
            setOrderBy(newOrderBy);
          }}
          order={order}
          orderBy={orderBy}
          showPager
          enableSorting
        />
      )}
    </>
  );
};

export default withMetricsByTaxonomy(MetricsTable);
